import {useEffect, useRef} from "react";
import {usePlanner} from "../index";


/**
 * This component handles the selection of a subset of continuous grid-day cells of a single resource (line).
 *
 * It expects the element that contains the entire selectable area to have the class tl-select-area,
 * the grid-day cells to have the data-tl-dts attribute set to the date in ISO format
 * and the container of the grid-day cells to have the data-tl-resource attribute set to the resource identifier.
 *
 * Whenever the user click on a grid-day cell and move the mouse over other grid-day cells the selection is updated.
 *
 * The selection is cleaned up when the user click outside the grid-day cells.
 * @param ref
 */
const useSelector = (ref) => {
    const {setSelectedRange, onSelect, setClickOutside} = usePlanner();

    // Use useRef hook to store the state of this component instead of the traditional useState
    // because we need to use the state in the event handlers, and we don't want to trigger a re-render.
    // A re-render is triggered by the handlers when the selection needs to be updated.
    const pivot = useRef();  // the first selected date, acts as a pivot
    const cursor = useRef();  // the second selected date, acts as a moving cursor
    const resource = useRef();  // the selected resource
    const mouseDown = useRef(false);  // true if the user is currently holding the mouse button down
    const selectFn = useRef(f=>f);  // the function that is called when the user select a range

    const reset = () => {
        pivot.current = null;
        cursor.current = null;
        resource.current = null;
        mouseDown.current = false;
    }

    const handleOnMouseDown = (e) => {
        e.preventDefault();
        if ("tlDts" in e.target.dataset) {
            const target = e.target;
            const res = target.closest('[data-tl-resource]');
            if(!!res){
                const date = e.target.dataset["tlDts"];
                pivot.current = date;
                cursor.current = date;
                resource.current = res.dataset["tlResource"];
                mouseDown.current = true;
                setSelectedRange({resource: res.dataset["tlResource"], from: date, to: date});
            }
        }
    }

    const handleOnMouseMove = (e) => {
        e.preventDefault();
        // Do nothing if the user has never clicked on a grid-day cell before
        if ( mouseDown.current ){
            if("tlDts" in e.target.dataset){
                const date = e.target.dataset["tlDts"];
                // Do nothing if the cursor is in the same date
                if(date !== cursor.current){
                    // Update the range
                    if(date > pivot.current ){
                        setSelectedRange({from: pivot.current, to: date});
                    }else if (date < pivot.current){
                        setSelectedRange({from: date, to: pivot.current});
                    }else{
                        setSelectedRange({from: date, to: date});
                    }
                    cursor.current = date;
                }
            }
        }
    }

    const handleOnMouseUp = (e) => {
        e.preventDefault();
        if(!!cursor.current && !!pivot.current){
            if( cursor.current < pivot.current ){
                selectFn.current(cursor.current, pivot.current, resource.current);
            }else if ( cursor.current > pivot.current ){
                selectFn.current(pivot.current, cursor.current, resource.current);
            }else if ( cursor.current === pivot.current ){
                selectFn.current(cursor.current, cursor.current, resource.current);
            }// else do nothing (pivot not defined because clicked outside and then moved in the grid)
        }
        mouseDown.current = false;
    }

    const handleOnMouseLeave = (e) => {
        e.preventDefault();
        mouseDown.current = false;
    }

    const handleClickOutside = (e) => {
        const selectArea = e?.target?.closest('.tl-select-area');
        if (!selectArea) {
            // The user clicked outside the selectable area and the selection needs to be reset. Avoid resetting the selection
            // when not needed to avoid a massive re-renders of the grid.
            if(!!cursor.current || !!pivot.current || !!resource.current || mouseDown.current){
                reset();
                setSelectedRange({from: null, to: null, resource: null});
            }
        }
    }

    // Update the selectFn ref when the onSelect function changes
    useEffect(
        () => {
            selectFn.current = onSelect;
        },
        [onSelect]
    )

    useEffect(
        () => {
            setClickOutside(() => handleClickOutside);
            const node = ref.current;

            node.addEventListener('mousedown', handleOnMouseDown);
            node.addEventListener('mousemove', handleOnMouseMove);
            node.addEventListener('mouseup', handleOnMouseUp);
            node.addEventListener('mouseleave', handleOnMouseLeave);

            return () => {
                node.removeEventListener('mousedown', handleOnMouseDown);
                node.removeEventListener('mousemove', handleOnMouseMove);
                node.removeEventListener('mouseup', handleOnMouseUp);
                node.removeEventListener('mouseleave', handleOnMouseLeave)
            }
        },
        []  // eslint-disable-line react-hooks/exhaustive-deps
    )
}

export default useSelector;